/**
 * Contenedor DOM donde se monta el widget de Cloudflare Turnstile.
 *
 * Vive fuera de Angular porque el reto corre en APP_INITIALIZER, antes de que
 * exista un componente donde montarlo.
 *
 * El contenedor se mantiene siempre adjunto y visible en el layout. Esconderlo
 * con `display:none` o desplazarlo fuera de pantalla (`top:-9999px`) impide que
 * el socio resuelva un reto interactivo, y Cloudflare interpreta ese reto sin
 * resolver como comportamiento de bot: es la familia de errores 600***. Con
 * `appearance: 'interaction-only'` el widget no pinta nada mientras no se
 * necesita interacción, así que estar adjunto no cuesta espacio visible.
 */
export class TurnstileChallengeOverlay {
	private static readonly STYLE_ID = 'cf-turnstile-overlay-styles';

	private root: HTMLElement | null = null;
	private host: HTMLElement | null = null;
	private message: HTMLElement | null = null;
	private title: HTMLElement | null = null;
	private retryButton: HTMLButtonElement | null = null;
	private retryHandler: (() => void) | null = null;

	/**
	 * Elemento donde Cloudflare monta el widget. Se crea al vuelo la primera vez
	 * y se reutiliza: cada intento monta y desmonta su widget dentro del mismo
	 * host, en lugar de crear y destruir nodos que dejen timers colgando.
	 */
	widgetHost(): HTMLElement {
		this.ensureMounted();
		return this.host as HTMLElement;
	}

	/** Estado por defecto: el widget corre sin pintar nada. */
	setIdle(): void {
		if (!this.root) {
			return;
		}
		this.root.className = 'cf-turnstile-overlay';
		this.hideRetry();
	}

	/**
	 * Cloudflare pidió interacción. Se abre el panel modal para que el widget sea
	 * visible y clickeable; sin esto el reto nunca se puede resolver.
	 */
	setInteractive(): void {
		this.ensureMounted();
		(this.root as HTMLElement).className =
			'cf-turnstile-overlay cf-turnstile-overlay--open';
		this.setCopy(
			'Verificación de seguridad',
			'Completa la verificación para continuar con tu solicitud.'
		);
		this.hideRetry();
	}

	/** El reto se agotó tras los reintentos automáticos: se ofrece uno manual. */
	showRetry(message: string, onRetry: () => void): void {
		this.ensureMounted();
		(this.root as HTMLElement).className =
			'cf-turnstile-overlay cf-turnstile-overlay--open';
		this.setCopy('No pudimos verificar tu navegador', message);
		this.retryHandler = onRetry;
		if (this.retryButton) {
			this.retryButton.hidden = false;
			this.retryButton.disabled = false;
			this.retryButton.focus();
		}
	}

	hideRetry(): void {
		this.retryHandler = null;
		if (this.retryButton) {
			this.retryButton.hidden = true;
		}
	}

	private setCopy(title: string, message: string): void {
		if (this.title) {
			this.title.textContent = title;
		}
		if (this.message) {
			this.message.textContent = message;
		}
	}

	private ensureMounted(): void {
		if (this.root?.isConnected) {
			return;
		}

		this.injectStyles();

		const root = document.createElement('div');
		root.className = 'cf-turnstile-overlay';
		root.setAttribute('role', 'dialog');
		root.setAttribute('aria-live', 'polite');

		const panel = document.createElement('div');
		panel.className = 'cf-turnstile-panel';

		const title = document.createElement('p');
		title.className = 'cf-turnstile-title';

		const message = document.createElement('p');
		message.className = 'cf-turnstile-message';

		const host = document.createElement('div');
		host.className = 'cf-turnstile-host';

		const retryButton = document.createElement('button');
		retryButton.type = 'button';
		retryButton.className = 'cf-turnstile-retry';
		retryButton.textContent = 'Reintentar';
		retryButton.hidden = true;
		retryButton.addEventListener('click', () => {
			retryButton.disabled = true;
			const handler = this.retryHandler;
			this.hideRetry();
			handler?.();
		});

		panel.append(title, message, host, retryButton);
		root.append(panel);
		document.body.append(root);

		this.root = root;
		this.host = host;
		this.title = title;
		this.message = message;
		this.retryButton = retryButton;
	}

	private injectStyles(): void {
		if (document.getElementById(TurnstileChallengeOverlay.STYLE_ID)) {
			return;
		}
		const style = document.createElement('style');
		style.id = TurnstileChallengeOverlay.STYLE_ID;
		style.textContent = `
.cf-turnstile-overlay {
	position: fixed;
	right: 16px;
	bottom: 16px;
	z-index: 2147483646;
}
.cf-turnstile-overlay .cf-turnstile-title,
.cf-turnstile-overlay .cf-turnstile-message { display: none; }
.cf-turnstile-overlay--open {
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 16px;
	background: rgba(17, 17, 17, 0.55);
}
.cf-turnstile-overlay--open .cf-turnstile-panel {
	max-width: 360px;
	width: 100%;
	padding: 24px;
	border-radius: 12px;
	background: #ffffff;
	box-shadow: 0 12px 32px rgba(0, 0, 0, 0.24);
	text-align: center;
	font-family: inherit;
}
.cf-turnstile-overlay--open .cf-turnstile-title {
	display: block;
	margin: 0 0 8px;
	font-size: 16px;
	font-weight: 600;
	color: #1a1a1a;
}
.cf-turnstile-overlay--open .cf-turnstile-message {
	display: block;
	margin: 0 0 16px;
	font-size: 14px;
	line-height: 1.4;
	color: #555555;
}
.cf-turnstile-overlay--open .cf-turnstile-host {
	display: flex;
	justify-content: center;
	min-height: 65px;
}
.cf-turnstile-retry {
	margin-top: 16px;
	padding: 10px 24px;
	border: 0;
	border-radius: 24px;
	background: #e2001a;
	color: #ffffff;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
}
.cf-turnstile-retry[disabled] { opacity: 0.6; cursor: default; }
`;
		document.head.append(style);
	}
}
